import React, { useState, useEffect } from 'react';
import Card from '../common/Card';
import Table from '../common/Table';
import DateRangePicker from '../common/DateRangePicker';
import Input from '../common/Input';
import Button from '../common/Button';
import api from '../../services/api';
import { formatDateTime } from '../../utils/formatters';
import '../../styles/admin.css';

const DEFAULT_PAGINATION = {
  page:  1,
  limit: 50,
  total: 0,
  pages: 0
};

const DEFAULT_FILTERS = {
  dateRange: {
    startDate: new Date(new Date().setDate(1)),
    endDate:   new Date()
  },
  business_id: '',
  action:      ''
};

const AuditLogs = () => {
  const [logs, setLogs]             = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [filters, setFilters]       = useState(DEFAULT_FILTERS);
  const [businesses, setBusinesses] = useState([]);
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);

  // Load businesses once on mount
  useEffect(() => {
    const fetchBusinesses = async () => {
      try {
        const response = await api.get('/admin/businesses?limit=100');
        setBusinesses(response.data.businesses || []);
      } catch (err) {
        console.error('Failed to load businesses:', err);
      }
    };
    fetchBusinesses();
  }, []);

  // Reload logs whenever page changes
  useEffect(() => {
    const fetchLogs = async () => {
      setLoading(true);
      setError(null);
      try {
        const params = {
          page:  pagination.page,
          limit: pagination.limit,
          from_date: filters.dateRange.startDate instanceof Date
            ? filters.dateRange.startDate.toISOString().split('T')[0]
            : filters.dateRange.startDate,
          to_date: filters.dateRange.endDate instanceof Date
            ? filters.dateRange.endDate.toISOString().split('T')[0]
            : filters.dateRange.endDate,
        };

        if (filters.business_id) params.business_id = filters.business_id;
        if (filters.action)      params.action      = filters.action;

        const response = await api.get('/admin/audit', { params });

        setLogs(response.data.logs || []);
        setPagination(prev => ({
          ...DEFAULT_PAGINATION,
          ...prev,
          ...(response.data.pagination || {}),
          page: pagination.page
        }));
      } catch (err) {
        console.error('Failed to load audit logs:', err);
        setError('Failed to load audit logs. Please try again.');
        setPagination(prev => ({ ...DEFAULT_PAGINATION, ...prev }));
      } finally {
        setLoading(false);
      }
    };

    fetchLogs();
  }, [pagination.page]); // only re-runs when page number changes

  const handleSearch = () => {
    // If already on page 1, force a reload by toggling a trigger
    // Otherwise resetting to page 1 will trigger the useEffect
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleReset = () => {
    setFilters(DEFAULT_FILTERS);
    setPagination(DEFAULT_PAGINATION);
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const columns = [
    {
      key: 'created_at',
      label: 'Timestamp',
      render: (value) => formatDateTime(value),
      width: '180px'
    },
    {
      key: 'user',
      label: 'User',
      render: (_, row) => (
        <div>
          <div>{row.first_name} {row.last_name}</div>
          <div className="text-small">{row.username}</div>
        </div>
      )
    },
    {
      key: 'business_name',
      label: 'Business',
      render: (value) => value || 'System'
    },
    {
      key: 'action',
      label: 'Action',
      render: (value) => (
        <span className={`action-badge ${value}`}>
          {value}
        </span>
      )
    },
    {
      key: 'entity_type',
      label: 'Entity',
      render: (value, row) => (
        <div>
          <div>{value || '-'}</div>
          <div className="text-small">ID: {row.entity_id || '-'}</div>
        </div>
      )
    },
    {
      key: 'ip_address',
      label: 'IP Address',
      render: (value) => value || '-'
    }
  ];

  const currentPage  = pagination?.page  ?? 1;
  const totalPages   = pagination?.pages ?? 0;
  const totalEntries = pagination?.total ?? 0;
  const limit        = pagination?.limit ?? 50;
  const showingFrom  = totalEntries === 0 ? 0 : ((currentPage - 1) * limit) + 1;
  const showingTo    = Math.min(currentPage * limit, totalEntries);

  return (
    <div className="audit-logs">
      <div className="page-header">
        <h1>Audit Logs</h1>
      </div>

      <Card className="filters-card">
        <div className="filters-grid">
          <DateRangePicker
            startDate={filters.dateRange.startDate}
            endDate={filters.dateRange.endDate}
            onChange={(range) => setFilters({ ...filters, dateRange: range })}
          />

          <div className="filter-group">
            <label>Business</label>
            <select
              value={filters.business_id}
              onChange={(e) => setFilters({ ...filters, business_id: e.target.value })}
            >
              <option value="">All Businesses</option>
              {businesses.map(business => (
                <option key={business.id} value={business.id}>
                  {business.business_name}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Action Type</label>
            <Input
              type="text"
              placeholder="e.g., login, create_sale"
              value={filters.action}
              onChange={(e) => setFilters({ ...filters, action: e.target.value })}
            />
          </div>

          <div className="filter-actions">
            <Button variant="primary" onClick={handleSearch}>
              Apply Filters
            </Button>
            <Button variant="secondary" onClick={handleReset}>
              Reset
            </Button>
          </div>
        </div>
      </Card>

      {error && (
        <div className="error-banner">
          {error}
          <Button
            size="sm"
            variant="secondary"
            onClick={() => setPagination(prev => ({ ...prev, page: prev.page }))}
          >
            Retry
          </Button>
        </div>
      )}

      <Card>
        <Table
          columns={columns}
          data={logs}
          loading={loading}
          emptyMessage="No audit logs found for the selected filters."
        />

        {totalEntries > 0 && (
          <div className="table-footer">
            <div className="pagination-info">
              Showing {showingFrom} to {showingTo} of {totalEntries} entries
            </div>
            <div className="pagination-controls">
              <Button
                size="sm"
                variant="secondary"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage <= 1}
              >
                Previous
              </Button>
              <span className="page-indicator">
                Page {currentPage} of {totalPages || 1}
              </span>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage >= totalPages}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

export default AuditLogs;